import { Features } from "@storytell/cp-client";
import { createEffect, createSignal } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { useAppUI } from "~/components/layouts/minimal/AppUIContext";
import type { KnowledgeSourcesState } from "../../PromptBar.types";

// Cookie key for model persistence
const MODEL_COOKIE_KEY = "pb_llm_model:v3";
const DEFAULT_MODEL = Features.Feature.MODEL_STORYTELL_AUTO_SELECT;

// Cookie helper functions
const getCookie = (key: string): string | null => {
  if (isServer) {
    const event = getRequestEvent();
    const cookieHeader = event?.request.headers.get("cookie");
    if (!cookieHeader) return null;

    const match = cookieHeader.match(new RegExp(`(^| )${key}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  }

  const match = document.cookie.match(new RegExp(`(^| )${key}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

const setCookie = (key: string, value: string): void => {
  if (isServer) return;
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 100); // 100 year expiry
  document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Strict`;
};

const loadModelFromCookie = (): Features.Feature => {
  const cookieValue = getCookie(MODEL_COOKIE_KEY);
  if (!cookieValue) return DEFAULT_MODEL;

  try {
    const modelValue = Number.parseInt(cookieValue, 10);
    // Check if the value is a valid model feature number
    // Model features are in the range 1000000-1599999
    if (modelValue >= 1000000 && modelValue <= 1599999) {
      return modelValue as Features.Feature;
    }
  } catch {
    // Invalid cookie value
  }

  return DEFAULT_MODEL;
};

const saveModelToCookie = (model: Features.Feature): void => {
  setCookie(MODEL_COOKIE_KEY, String(model));
};

/**
 * Hook for managing knowledge sources state and interactions
 * Uses layout context for persistence across SSR
 */
export const useKnowledgeSourcesDisplay = () => {
  const { state, actions } = useAppUI();
  const [showTooltip, setShowTooltip] = createSignal(false);

  // Initialize model from cookie or default
  const [selectedModel, setSelectedModelInternal] = createSignal<Features.Feature>(loadModelFromCookie());

  // Custom setter that also updates cookie
  const setSelectedModel = (model: Features.Feature) => {
    setSelectedModelInternal(model);
    saveModelToCookie(model);
  };

  const toggleTooltip = () => setShowTooltip(!showTooltip());
  const hideTooltip = () => setShowTooltip(false);

  const toggleTraining = () => actions.toggleKnowledgeSource("training");
  const toggleWeb = () => actions.toggleKnowledgeSource("web");
  const toggleMyKnowledge = () => actions.toggleKnowledgeSource("myKnowledge");
  const toggleMemories = () => actions.toggleKnowledgeSource("memories");

  const knowledgeState: KnowledgeSourcesState & {
    setShowTooltip: (show: boolean) => void;
    toggleTooltip: () => void;
    hideTooltip: () => void;
    toggleTraining: () => void;
    toggleWeb: () => void;
    toggleMyKnowledge: () => void;
    toggleMemories: () => void;
    setSelectedModel: (model: Features.Feature) => void;
  } = {
    // Use layout context state for persistence
    training: () => state.promptBar.knowledgeSources.training,
    web: () => state.promptBar.knowledgeSources.web,
    myKnowledge: () => state.promptBar.knowledgeSources.myKnowledge,
    memories: () => state.promptBar.knowledgeSources.memories,
    // Local state for UI-only features
    showTooltip,
    selectedModel,
    setShowTooltip,
    toggleTooltip,
    hideTooltip,
    toggleTraining,
    toggleWeb,
    toggleMyKnowledge,
    toggleMemories,
    setSelectedModel,
  };

  return knowledgeState;
};
